import { Link, useLocation } from 'react-router-dom';
import { useLocale, SupportedLocale } from '@platform/core';
import { useSimpleAuth } from '../contexts/SimpleAuthContext';
import { useBudget } from '../contexts/BudgetContext';

const LANGUAGES: { code: SupportedLocale; label: string }[] = [
  { code: 'he', label: 'עברית' },
  { code: 'en', label: 'English' },
];

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { simpleUser, signOut } = useSimpleAuth();
  const { t, locale, setLocale, direction } = useLocale();
  const { budgets, currentBudget, selectBudget, isSaving, currentCompany, companies, selectCompany } = useBudget();
  const location = useLocation();

  const navItems = [
    { path: '/', label: t('nav.dashboard'), icon: '📊' },
    { path: '/data', label: t('nav.data'), icon: '📝' },
    { path: '/charts', label: t('nav.charts'), icon: '📈' },
    { path: '/projects', label: t('nav.projects'), icon: '📋' },
    { path: '/settings', label: t('nav.settings'), icon: '⚙️' },
  ];

  return (
    <div className="app-layout" dir={direction}>
      <header className="app-header">
        <div className="header-left">
          <Link to="/" className="logo">
            <span className="logo-icon">🔑</span>
            <span className="logo-text">TurnKeyOS <span style={{color:'#3b82f6',fontWeight:700}}>Budget</span></span>
          </Link>

          {companies.length > 1 && (
            <select
              className="company-selector"
              value={currentCompany?.id || ''}
              onChange={(e) => selectCompany(e.target.value)}
            >
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.CompanyName}
                </option>
              ))}
            </select>
          )}

          {isSaving && <span className="saving-indicator">{t('general.saving')}</span>}

        </div>

        <nav className="header-nav">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
            >
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="header-right">
          <select
            className="language-selector"
            value={locale}
            onChange={(e) => setLocale(e.target.value as SupportedLocale)}
          >
            {LANGUAGES.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.label}
              </option>
            ))}
          </select>
          <span className="org-name">{currentCompany?.CompanyName || ''}</span>
          <span className="user-name">{simpleUser?.displayName || simpleUser?.email}</span>
          <button className="btn btn-logout" onClick={signOut}>
            {t('nav.logout')}
          </button>
        </div>
      </header>

      {budgets.length > 1 && (
        <div className="budget-tabs">
          {budgets.map((budget) => (
            <button
              key={budget.id}
              className={`budget-tab ${currentBudget?.id === budget.id ? 'active' : ''}`}
              onClick={() => selectBudget(budget.id)}
            >
              {budget.name}
            </button>
          ))}
        </div>
      )}

      <main className="app-main">
        {children}
      </main>

      <style>{`
        .app-layout {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
        }

        .app-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1.5rem;
          background: var(--color-surface);
          border-bottom: 1px solid var(--color-border);
          box-shadow: var(--shadow-sm);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          text-decoration: none;
          color: var(--color-text);
          font-weight: 700;
        }

        .logo-icon { font-size: 1.5rem; }
        .logo-text { font-size: 1.25rem; }

        .company-selector, .budget-selector, .language-selector {
          padding: 0.5rem 0.75rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          background: var(--color-surface);
          font-size: 0.875rem;
        }

        .language-selector {
          min-width: 80px;
        }

        .saving-indicator {
          font-size: 0.75rem;
          color: var(--color-text-secondary);
        }

        .header-nav {
          display: flex;
          gap: 0.5rem;
        }

        .nav-link {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.5rem 0.75rem;
          text-decoration: none;
          color: var(--color-text-secondary);
          border-radius: var(--radius);
          font-size: 0.875rem;
          transition: all 0.15s;
        }

        .nav-link:hover {
          background: var(--color-background);
          color: var(--color-text);
        }

        .nav-link.active {
          background: var(--color-primary);
          color: white;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .org-name {
          font-weight: 500;
          font-size: 0.875rem;
        }

        .user-name {
          font-size: 0.875rem;
          color: var(--color-text-secondary);
        }

        .btn-logout {
          padding: 0.375rem 0.75rem;
          font-size: 0.75rem;
          color: var(--color-error);
          border-color: var(--color-error);
        }

        .budget-tabs {
          display: flex;
          gap: 0;
          background: var(--color-surface);
          border-bottom: 2px solid var(--color-border);
          padding: 0 1.5rem;
          overflow-x: auto;
        }

        .budget-tab {
          padding: 0.625rem 1.25rem;
          border: none;
          border-bottom: 3px solid transparent;
          background: transparent;
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--color-text-secondary);
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.15s;
          margin-bottom: -2px;
        }

        .budget-tab:hover {
          color: var(--color-text);
          background: var(--color-background);
        }

        .budget-tab.active {
          color: var(--color-primary);
          border-bottom-color: var(--color-primary);
        }

        .app-main {
          flex: 1;
          padding: 1.5rem;
        }

        @media (max-width: 768px) {
          .app-header {
            flex-wrap: wrap;
            gap: 0.75rem;
          }

          .header-nav {
            order: 3;
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};
